import { type FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { createApi } from "@reduxjs/toolkit/query/react";
import { format } from "date-fns";

import { ISO8601_DATE_FORMAT } from "@constants";

import { baseQuery } from "../../../shared/api/base-query";
import { createQueryFn } from "../../../shared/api/create-query-fn";
import { OrderGroup } from "../../../shared/interface";
import { buildFilterCondition, buildFilterExpression } from "../../../shared/utils";
import { TechopsRequest } from "../containers/tech-operations-list/techop-list-utils";
import { TechOperation } from "../shared/models/tech-operation";

import { EKOCROP_WEB_APP_SOURCE_SYSTEM_CODE } from "./constants";
import { executeUpsertTechOp } from "./execute-upsert-tech-op";
import { getTechOperationFromPagedResponse } from "./get-tech-operation-from-paged-response";
import {
  TechOpsPagedResponseDto,
  transformAgregatedsV2ErrorResponse,
} from "./techop.rtkq.types";

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TECH_OPERATION_NOT_FOUND_ERROR: FetchBaseQueryError = {
  status: 404,
  data: null,
};

function isValidGuid(value: string | undefined): value is string {
  return value !== undefined && GUID_PATTERN.test(value);
}

/** Удаление требует автора операции: бэкенд пишет его в журнал изменений. */
export interface DeleteTechopArgs {
  techopId: string;
  appUserId: string;
}

export const techopsApiSlice = createApi({
  reducerPath: "api-techop",
  baseQuery,
  tagTypes: ["TechopsList"],
  endpoints: (builder) => ({
    techopsList: builder.query<
      { items: TechOperation[]; totalCount: number; totalArea: number },
      TechopsRequest
    >({
      query: (searchParams: TechopsRequest) => {
        const { paginationState, onlyOriginal } = searchParams;
        const { limit, offset, order } = paginationState;
        const pageNumber = Math.floor(offset / limit) + 1;
        const sort = order.length > 0 ? buildSortString(order) : undefined;
        const filter = buildTechopsFilter(searchParams);
        return {
          url: "TechOperationsV2",
          params: {
            pageNumber,
            pageSize: limit,
            sort,
            filter,
            ...(onlyOriginal !== undefined && { onlyOriginal }),
          },
          method: "GET",
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...result.items.map(({ id }) => ({ type: "TechopsList" as const, id })),
              { type: "TechopsList" as const, id: "LIST" },
            ]
          : [];
      },
      transformResponse: (response: TechOpsPagedResponseDto) => ({
        items: response.data.map((dto) => TechOperation.fromDto(dto)),
        totalCount: response.meta.totalCount,
        totalArea: response.aggregations?.totalArea ?? 0,
      }),
    }),

    fetchTechop: builder.query<TechOperation, string | undefined>({
      queryFn: async (techopId, _queryApi, _extraOptions, executeBaseQuery) => {
        if (!isValidGuid(techopId)) {
          return { error: TECH_OPERATION_NOT_FOUND_ERROR };
        }

        const response = await executeBaseQuery({
          url: "TechOperationsV2",
          method: "GET",
          params: {
            pageNumber: 1,
            pageSize: 1,
            filter: buildFilterCondition("id", "==", techopId),
            onlyOriginal: true,
          },
        });

        if (response.error !== undefined) {
          return { error: response.error };
        }

        const techOperation = getTechOperationFromPagedResponse(response.data);
        if (techOperation === undefined) {
          return {
            error: TECH_OPERATION_NOT_FOUND_ERROR,
          };
        }

        return { data: techOperation };
      },
      providesTags: (result) =>
        result ? [{ type: "TechopsList" as const, id: result.id }] : [],
    }),

    addTechop: builder.mutation<TechOperation, TechOperation>({
      queryFn: createQueryFn(executeUpsertTechOp),
      invalidatesTags: (result) => [
        { type: "TechopsList" as const, id: "LIST" },
        ...(result ? [{ type: "TechopsList" as const, id: result.id }] : []),
      ],
    }),

    deleteTechop: builder.mutation<number, DeleteTechopArgs>({
      query: ({ techopId, appUserId }) => {
        return {
          url: "TechOperationsV2",
          method: "DELETE",
          headers: {
            "Content-Type": "application/json;charset=utf-8",
          },
          params: {
            appUserId,
            sourceSystemCode: EKOCROP_WEB_APP_SOURCE_SYSTEM_CODE,
          },
          body: JSON.stringify([techopId]),
        };
      },
      transformResponse: (response: { count?: number }) => {
        return response?.count ?? 1;
      },
      transformErrorResponse: transformAgregatedsV2ErrorResponse,
      invalidatesTags: (_result, _error, { techopId }) => [
        { type: "TechopsList" as const, id: "LIST" },
        { type: "TechopsList" as const, id: techopId },
      ],
    }),

    updateTechop: builder.mutation<TechOperation, TechOperation>({
      queryFn: createQueryFn(executeUpsertTechOp),
      invalidatesTags: (result) => [
        { type: "TechopsList" as const, id: "LIST" },
        ...(result ? [{ type: "TechopsList" as const, id: result.id }] : []),
      ],
    }),
  }),
});

function buildTechopsFilter(searchParams: TechopsRequest): string {
  const { filtersState, seasonId, farmId } = searchParams;
  const { from, to, farmLandId, cropTypeId, groupId, subGroupId } = filtersState;

  return buildFilterExpression(
    buildFilterCondition("seasonId", "==", seasonId),
    buildFilterCondition("farmId", "==", farmId),
    from && buildFilterCondition("startDate", ">=", format(from, ISO8601_DATE_FORMAT)),
    to && buildFilterCondition("finishDate", "<=", format(to, ISO8601_DATE_FORMAT)),
    farmLandId && buildFilterCondition("farmLandId", "==", farmLandId),
    cropTypeId && buildFilterCondition("cropTypeId", "==", cropTypeId),
    groupId && buildFilterCondition("techOperationGroupId", "==", groupId),
    subGroupId && buildFilterCondition("techOperationSubGroupId", "==", subGroupId)
  );
}

function buildSortString(order: OrderGroup): string {
  return order.map(([field, direction]) => `${String(field)} ${direction}`).join(", ");
}

export const {
  useTechopsListQuery,
  useFetchTechopQuery,
  useLazyFetchTechopQuery,
  useAddTechopMutation,
  useDeleteTechopMutation,
  useUpdateTechopMutation,
} = techopsApiSlice;

export const useTechopsListQueryState =
  techopsApiSlice.endpoints.techopsList.useQueryState;

export const useFetchTechopQueryState =
  techopsApiSlice.endpoints.fetchTechop.useQueryState;
